import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { Alert } from '../../components/Alert';
import { EmailDeliveryGuidance } from '../../components/EmailDeliveryGuidance';
import { authApi } from '../../features/auth/auth.api';
import { toDisplayMessage, toFieldErrors } from '../../lib/error-message';
import { paths } from '../../routes/paths';

import styles from './AuthForm.module.css';
import { AuthFormField } from './AuthFormField';
import { AuthLayout } from './AuthLayout';

/** Matches default backend password policy (env.schema defaults). */
const PASSWORD_HINT =
  'At least 12 characters, including upper and lower case, a digit and a symbol.';

export function RegisterPage(): React.JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});

    if (password !== confirmation) {
      setFieldErrors({ confirmation: 'The two passwords do not match.' });
      return;
    }

    setBusy(true);

    try {
      await authApi.register(email, password);
      setSubmitted(true);
    } catch (caught: unknown) {
      setError(toDisplayMessage(caught, 'Unable to create the account. Please try again.'));
      setFieldErrors(toFieldErrors(caught));
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <AuthLayout
        title="Check your email"
        description="Verify your address to activate your Seamontech account."
        footer={
          <>
            <p>
              Didn&apos;t receive the verification email?{' '}
              <Link to={paths.resendVerification}>Resend verification email</Link>
            </p>
            <p>
              <Link to={paths.login}>Back to sign in</Link>
            </p>
          </>
        }
      >
        <Alert tone="success" title="Account created">
          Check your email to verify your account. We sent a verification link to{' '}
          <strong>{email}</strong>. The link expires after 24 hours.
        </Alert>
        <EmailDeliveryGuidance />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Create your account"
      description="Create your account to begin and manage your solar journey."
      heroHeadline="Start your solar journey with Seamontech."
      heroLead="Create an account to apply, sign your contract, and manage repayments."
      scrollableForm
      footer={
        <p>
          Already have an account? <Link to={paths.login}>Sign in</Link>
        </p>
      }
    >
      <form className={styles.form} onSubmit={(event) => void handleSubmit(event)} noValidate>
        {error !== null && <Alert tone="error">{error}</Alert>}

        <AuthFormField
          label="Email address"
          inputType="email"
          name="email"
          icon="mail"
          value={email}
          onChange={(event) => {
            setEmail(event.target.value);
          }}
          autoComplete="email"
          placeholder="you@example.com"
          error={fieldErrors.email}
          required
        />

        <AuthFormField
          label="Password"
          inputType="password"
          name="password"
          icon="lock"
          value={password}
          onChange={(event) => {
            setPassword(event.target.value);
          }}
          autoComplete="new-password"
          placeholder="Create a strong password"
          hint={PASSWORD_HINT}
          error={fieldErrors.password}
          allowPasswordToggle
          required
        />

        <AuthFormField
          label="Confirm password"
          inputType="password"
          name="confirmation"
          icon="lock"
          value={confirmation}
          onChange={(event) => {
            setConfirmation(event.target.value);
          }}
          autoComplete="new-password"
          placeholder="Re-enter your password"
          error={fieldErrors.confirmation}
          allowPasswordToggle
          required
        />

        <button className={styles.submitButton} type="submit" disabled={busy} aria-busy={busy}>
          {busy ? 'Creating your account…' : 'Create account'}
          {!busy && (
            <span className={styles.submitArrow} aria-hidden="true">
              →
            </span>
          )}
        </button>
      </form>
    </AuthLayout>
  );
}
